import {type ReactNode, useMemo} from 'react'
import {Platform, Pressable, View} from 'react-native'
import {Image} from 'expo-image'
import {type AppBskyFeedDefs, type ModerationDecision} from '@atproto/api'
import {Plural, Trans, useLingui} from '@lingui/react/macro'

import {
  useCapsuleByPostQuery,
  useCapsuleByUriQuery,
} from '#/state/queries/capsule'
import {MODULE_MIN_CAPSULES} from '#/lib/modulePlaylist'
import {sanitizeHandle} from '#/lib/strings/handles'
import {UserAvatar} from '#/view/com/util/UserAvatar'
import {atoms as a, useTheme} from '#/alf'
import {CapsulePostCard} from '#/components/capsules/CapsulePostCard'
import {FlashBookFeedEmbed} from '#/components/flashbooks/FlashBookFeedEmbed'
import {MediaInsetBorder} from '#/components/MediaInsetBorder'
import {ModulePostCard} from '#/components/modules/ModulePostCard'
import {Text} from '#/components/Typography'
import {DraftBadge} from './WorkCardBadge'
import {
  BOOK_ASPECT,
  CAPSULE_ASPECT,
  MODULE_ASPECT,
  workAspect,
  type WorkEntry,
  type WorkKind,
} from './types'

/**
 * One cell of a work grid, drawn from the post the work was published as.
 *
 * The cards are the app's own — `CapsulePostCard` for a capsule, `FlashBookFeedEmbed`
 * for a book — which is the point: a capsule in a classroom and the same capsule in
 * the feed are one card, and a later change to either is a change to both, for free.
 *
 * Three things this cell takes that the feed's card cannot supply:
 *
 *  • **`badge`** — the one fact about where this work lives that the card has no
 *    reason to know (a room's own capsule, a draft). The caller decides which, if
 *    any, because only the caller knows the question the grid is answering.
 *  • **`testID`** — a classroom's grid and a profile's grid are driven by different
 *    recipes, so each names its own cells.
 *  • **`renderPostless`** — what to draw when an entry has no post to play. A
 *    classroom has its own answer (a seeded demo card, or a capsule made for a room
 *    before those were published as posts, see `ClassroomCapsuleCard`); a profile
 *    has none, because the only post-less work it knows is a draft, and drafts are a
 *    cell of their own (`DraftCell`).
 */

export function CapsuleCell({
  entry,
  post,
  modui,
  isPending,
  badge,
  testID,
  goneTestID,
  unpublishedTestID,
  renderPostless,
}: {
  entry: WorkEntry
  post: AppBskyFeedDefs.PostView | undefined
  modui: ModerationDecision | undefined
  isPending: boolean
  /** Drawn over the card: the one thing this cell cannot say by itself. */
  badge?: ReactNode
  /** The wrapper around the card itself, named by the grid that drives it. */
  testID?: string
  /** The cell for an entry whose post is gone. See `FlashbookCell` for why they
   * are spelled out rather than derived from `testID`. */
  goneTestID?: string
  /** The cell for an entry that was never published. */
  unpublishedTestID?: string
  /**
   * The cell for an entry with a capsule record and no post to play it from.
   *
   * Handed the record's address rather than reading it off the entry, so that the one
   * cell that can be reached this way is the one place that has to know it is there.
   */
  renderPostless?: (capsuleUri: string) => ReactNode
}) {
  const {t: l} = useLingui()
  const postRecord = post?.record as Record<string, unknown> | undefined
  const capsuleRefUri = (postRecord?.capsule as {uri?: string} | undefined)?.uri
  /** No address on the post: the capsule has to be found by scanning for it. */
  const needsScan = !capsuleRefUri && postRecord?.type === 'capsule'

  const {data: byUri} = useCapsuleByUriQuery(capsuleRefUri)
  const {data: byPost} = useCapsuleByPostQuery(
    needsScan ? (post?.uri ?? '') : '',
    needsScan ? (post?.author.did ?? '') : '',
  )
  const capsuleRecord = byUri ?? byPost

  const capsule = useMemo(() => {
    const slideCount =
      capsuleRecord?.slideCount ??
      (postRecord?.slideCount as number | undefined)
    if (!capsuleRecord && slideCount == null) return undefined
    return {
      ...(capsuleRecord ?? {}),
      ...(slideCount != null ? {slideCount} : {}),
    } as Record<string, unknown>
  }, [capsuleRecord, postRecord])

  if (post) {
    // No decision yet means the preferences are still arriving, and a capsule
    // is content: it waits rather than flashing ungraded.
    if (!modui) return <EmptyCell />
    return (
      <View testID={testID} style={[a.relative]}>
        <CapsulePostCard
          capsulePost={post}
          capsule={capsule}
          moderation={modui}
        />
        {badge}
      </View>
    )
  }
  if (entry.postUri) {
    // Its address is known and there is nothing at it: either the fetch is
    // still out, or the capsule was deleted where it lived.
    if (isPending) return <EmptyCell />
    return (
      <PlaceholderCell
        testID={goneTestID}
        entry={entry}
        title={entry.capsuleTitle ?? firstLine(entry.text)}
        message={l`This capsule is no longer here.`}
      />
    )
  }
  if (entry.capsuleUri && renderPostless) {
    return <>{renderPostless(entry.capsuleUri)}</>
  }
  // Written and never published, so there is nothing to play. The card says
  // exactly that rather than offering a door that does not open.
  return (
    <PlaceholderCell
      testID={unpublishedTestID}
      entry={entry}
      title={entry.capsuleTitle ?? firstLine(entry.text)}
      message={l`Never published, so there is nothing to play yet.`}
    />
  )
}

/**
 * One cell of a book grid: the app's own book card.
 *
 * `FlashBookFeedEmbed` is the card a feed, a thread and a message draw a published
 * book with — its cover, the theme it was written in, how long it is, how much of it
 * is work, and the way to start reading — so a book in a grid is the same object as a
 * book anywhere else. It reaches the record the same way the feed's card does, from
 * the post's own back-reference, so nothing about the grid changes how a book is read.
 *
 * The badge sits under the card's own kind chip rather than on it: the cover prints
 * "Storybook" or "Video" in the corner a badge would otherwise take (see
 * `WorkCardBadge`).
 */
export function FlashbookCell({
  entry,
  post,
  isPending,
  badge,
  testID,
  goneTestID,
  unpublishedTestID,
}: {
  entry: WorkEntry
  post: AppBskyFeedDefs.PostView | undefined
  isPending: boolean
  badge?: ReactNode
  /** The wrapper around the card itself, named by the grid that drives it. */
  testID?: string
  /**
   * The two cells for a book with no card to draw.
   *
   * Spelled out rather than derived from `testID` because these are the names the
   * browser recipes already drive a classroom by, and a recipe that had to be
   * rewritten to keep measuring the same screen would be the extraction's cost
   * rather than its benefit.
   */
  goneTestID?: string
  unpublishedTestID?: string
}) {
  const {t: l} = useLingui()

  if (post) {
    return (
      <View testID={testID} style={[a.relative]}>
        <FlashBookFeedEmbed post={post} />
        {badge}
      </View>
    )
  }
  if (entry.postUri) {
    // Its address is known and there is nothing at it: either the fetch is still
    // out, or the book was deleted where it lived.
    if (isPending) return <EmptyCell aspectRatio={BOOK_ASPECT} />
    return (
      <PlaceholderCell
        testID={goneTestID}
        entry={entry}
        title={entry.flashbookTitle ?? firstLine(entry.text)}
        message={l`This book is no longer here.`}
        aspectRatio={BOOK_ASPECT}
      />
    )
  }
  /*
   * A book entry with no post should not exist on a screen that lists published
   * work: a book with no post is a draft, and a draft is drawn by `DraftCell`. Said
   * rather than assumed, because an entry written by hand — or by a build that got
   * this wrong — should still see a card in its place with the reason on it rather
   * than a hole.
   */
  return (
    <PlaceholderCell
      testID={unpublishedTestID}
      entry={entry}
      title={entry.flashbookTitle ?? firstLine(entry.text)}
      message={l`Never published, so there is nothing to read yet.`}
      aspectRatio={BOOK_ASPECT}
    />
  )
}

/**
 * One cell of a module grid: the compact playlist card.
 *
 * Deliberately *not* the feed's card. `ModuleFeedEmbed` is a carousel, a
 * description and two actions — a whole screen's worth of a row — and a grid is
 * eleven more cells beside it. So a module in a grid is drawn by its own card
 * (`ModulePostCard`), which keeps the two things a reader chooses by (the row of
 * capsules, and what a run is worth) and drops the rest to the module's own page.
 *
 * Everything else is the shape the two cells above use, for the reasons they use
 * it: the record is reached from the post's own back-reference, the cell stands in
 * for a card while the post is on its way, and work whose post is gone says so
 * rather than showing a hole.
 */
export function ModuleCell({
  entry,
  post,
  isPending,
  badge,
  testID,
  goneTestID,
  unpublishedTestID,
}: {
  entry: WorkEntry
  post: AppBskyFeedDefs.PostView | undefined
  isPending: boolean
  badge?: ReactNode
  testID?: string
  goneTestID?: string
  unpublishedTestID?: string
}) {
  const {t: l} = useLingui()

  if (post) {
    return (
      <View testID={testID} style={[a.relative]}>
        <ModulePostCard post={post} />
        {badge}
      </View>
    )
  }
  if (entry.postUri) {
    if (isPending) return <EmptyCell aspectRatio={MODULE_ASPECT} />
    return (
      <PlaceholderCell
        testID={goneTestID}
        entry={entry}
        title={entry.moduleTitle ?? firstLine(entry.text)}
        message={l`This module is no longer here.`}
        aspectRatio={MODULE_ASPECT}
      />
    )
  }
  /*
   * A module with no post is a draft, and a draft has its own cell — so this is
   * the same last resort the book cell keeps, said for the same reason: an entry
   * written by hand still deserves a card that explains itself rather than a hole
   * in the grid.
   */
  return (
    <PlaceholderCell
      testID={unpublishedTestID}
      entry={entry}
      title={entry.moduleTitle ?? firstLine(entry.text)}
      message={l`Never published, so there is nothing to play yet.`}
      aspectRatio={MODULE_ASPECT}
    />
  )
}

/**
 * The cell for work the creator has not published yet.
 *
 * A draft has no post, so none of the cards above can draw it: there is no embed to
 * play and nothing for a thread to answer. What it has instead is everything the
 * creator wrote — the title, and a book's cover or a capsule's own facts — which is
 * why this cell is drawn from the *record* the way the studios' draft lists are, and
 * not from the feed.
 *
 * It is pressable, and pressing it opens the work in its studio: a draft is the one
 * kind of work whose whole purpose is to be continued. The badge says so in the
 * corner, because a reader looking at a profile has to be able to tell a draft from
 * something that is out in the world — and on a profile only the creator ever sees
 * one (see `#/lib/profileWork`).
 */
export function DraftCell({
  kind,
  title,
  coverUri,
  size,
  testID,
  onPress,
}: {
  kind: WorkKind
  title: string
  /** A book's cover still, when the record has one. */
  coverUri?: string
  /** Slides, or pages — whichever this kind counts in. */
  size?: number
  testID?: string
  onPress: () => void
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const aspectRatio = workAspect(kind)

  /*
   * A module draft draws its playlist instead of a hole.
   *
   * A capsule's draft has slides and a book's has pages, so a cover-less draft of
   * either is a cover-less draft. A module has no artwork at all — what it has is
   * *places* — so the same blank box said nothing about it: an empty playlist and
   * a finished one were the same dark rectangle with the word *Draft* on it, which
   * is exactly the state a creator most needs to see at a glance (their own module,
   * just started, nothing in it). What fills the box instead is the shape of the
   * thing: one pip per place, the filled ones in the playlist's own colour, and the
   * words to go with it when there is something to say — *No capsules yet* on a
   * module that has just been named, *3 of 6 capsules* on one being built.
   */
  const emptyModule = kind === 'module' && !coverUri
  const places = MODULE_MIN_CAPSULES
  const filled = Math.min(size ?? 0, places)

  /*
   * `0` is a fact for a module and not for a capsule or a book.
   *
   * A record with no slides or no pages is one nobody has begun, and a chip saying
   * *0 slides* would be noise; a module with no capsules is a playlist with nothing
   * in it, and the card is the only place its author can see that without opening
   * it. A module says it in the box instead — the pips and the words above — so the
   * chip keeps to its own job and the two never say the same thing twice.
   */
  const showSize = size != null && size > 0

  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={l`Keep writing ${title}`}
      accessibilityHint={l`Opens this draft in its studio`}
      onPress={onPress}
      style={[a.flex_col, Platform.OS === 'web' ? {cursor: 'pointer'} : null]}>
      <View
        style={[
          a.rounded_md,
          a.overflow_hidden,
          {
            aspectRatio,
            backgroundColor: t.atoms.bg_contrast_50.backgroundColor,
          },
        ]}>
        {coverUri ? (
          <Image
            source={{uri: coverUri}}
            style={[a.w_full, a.h_full]}
            contentFit="cover"
            accessibilityIgnoresInvertColors
          />
        ) : null}

        <DraftBadge />

        {emptyModule ? (
          <View
            style={[
              a.flex_1,
              a.justify_center,
              a.align_center,
              {paddingHorizontal: 16, gap: 10},
            ]}>
            <View style={[a.flex_row, a.w_full, {gap: 4}]}>
              {Array.from({length: places}).map((_, index) => (
                <View
                  key={index}
                  style={[
                    {height: 6, flex: 1, borderRadius: 999},
                    index < filled
                      ? {backgroundColor: t.palette.primary_500}
                      : t.atoms.bg_contrast_100,
                  ]}
                />
              ))}
            </View>
            {filled < places ? (
              <Text
                style={[
                  a.text_xs,
                  a.text_center,
                  t.atoms.text_contrast_medium,
                ]}>
                {filled > 0 ? (
                  <Trans>
                    {filled} of {places} capsules
                  </Trans>
                ) : (
                  <Trans>No capsules yet</Trans>
                )}
              </Text>
            ) : null}
          </View>
        ) : null}

        {showSize ? (
          <View
            style={[
              a.absolute,
              {
                top: 6,
                right: 6,
                backgroundColor: 'rgba(0,0,0,0.55)',
                borderRadius: 20,
                paddingHorizontal: 8,
                paddingVertical: 3,
              },
            ]}>
            <Text
              style={[
                a.text_xs,
                a.font_bold,
                {color: 'white', lineHeight: 14},
              ]}>
              {kind === 'flashbook' ? (
                <Plural value={size ?? 0} one="# page" other="# pages" />
              ) : kind === 'module' ? (
                <Plural value={size ?? 0} one="# capsule" other="# capsules" />
              ) : (
                <Plural value={size ?? 0} one="# slide" other="# slides" />
              )}
            </Text>
          </View>
        ) : null}

        {/*
          The title always sits on the card's own foot rather than only in the
          caption, because the caption of a cover-less card would be the title twice
          over: this way the card reads the same whether the work has artwork or not,
          which is also how the studio's draft list reads.
        */}
        <View
          style={[
            a.absolute,
            {
              left: 0,
              right: 0,
              bottom: 0,
              paddingHorizontal: 8,
              paddingVertical: 6,
              backgroundColor: 'rgba(0,0,0,0.55)',
            },
          ]}>
          <Text
            emoji
            numberOfLines={2}
            style={[
              a.text_sm,
              a.font_semi_bold,
              {color: 'white'},
              !coverUri && a.text_center,
            ]}>
            {title || l`Untitled`}
          </Text>
        </View>
      </View>

      <View style={[a.pr_xs, {paddingTop: 6, gap: 4}]}>
        <Text
          style={[a.text_sm, a.leading_tight, t.atoms.text_contrast_medium]}
          numberOfLines={2}>
          {l`Not published yet.`}
        </Text>
      </View>
    </Pressable>
  )
}

/** A card's shape while its post is on its way: the same box, empty. */
export function EmptyCell({
  aspectRatio = CAPSULE_ASPECT,
}: {
  aspectRatio?: number
}) {
  const t = useTheme()
  return <View style={[a.rounded_md, t.atoms.bg_contrast_25, {aspectRatio}]} />
}

/**
 * The cell for work that cannot be opened.
 *
 * It keeps the proportions of the card it stands in for and the title and author
 * under it, so a grid whose work is not all playable still reads as a grid — and
 * the box itself carries the reason there is nothing to open.
 */
export function PlaceholderCell({
  entry,
  title,
  message,
  testID,
  aspectRatio = CAPSULE_ASPECT,
}: {
  entry: WorkEntry
  title: string | undefined
  message: React.ReactNode
  testID?: string
  aspectRatio?: number
}) {
  const t = useTheme()

  return (
    <View testID={testID} style={[a.flex_col]}>
      <View
        style={[
          a.justify_center,
          a.align_center,
          a.p_md,
          a.rounded_md,
          a.overflow_hidden,
          a.w_full,
          t.atoms.bg_contrast_50,
          {aspectRatio},
        ]}>
        {title ? (
          <Text
            emoji
            numberOfLines={3}
            style={[
              a.text_md,
              a.font_bold,
              a.text_center,
              t.atoms.text_contrast_high,
            ]}>
            {title}
          </Text>
        ) : null}
        <Text
          style={[
            a.text_xs,
            a.text_center,
            {paddingTop: 4},
            t.atoms.text_contrast_medium,
          ]}>
          {message}
        </Text>
      </View>

      <View style={[a.pr_xs, {paddingTop: 6, gap: 4}]}>
        <View style={[a.flex_row, a.gap_xs, a.align_center]}>
          <View style={[a.relative, a.rounded_full, {width: 20, height: 20}]}>
            <UserAvatar type="user" size={20} avatar={entry.authorAvatar} />
            <MediaInsetBorder />
          </View>
          <Text
            style={[
              a.flex_1,
              a.text_sm,
              a.leading_tight,
              t.atoms.text_contrast_medium,
            ]}
            numberOfLines={1}>
            {sanitizeHandle(entry.authorHandle, '@')}
          </Text>
        </View>
      </View>
    </View>
  )
}

/** The first line of a body of text, as a card's title. */
export function firstLine(text: string): string {
  const [line] = text.trim().split('\n')
  return line?.trim() ?? ''
}
